import { Button, Spinner } from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";
import { useCallback, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert } from "~/components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "~/components/modal/Dialog";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { RyftCardPaymentProvider } from "./RyftCardPaymentProvider";
import { RyftCardForm } from "./RyftCardForm";
import { useRyftPayment } from "./useRyftPayment";
import { ServerStateKeys } from "~/hooks/api/v2/ServerStateKeys";
import { useFirebaseUser } from "~/hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";

type PayWithRyftButtonProps = {
  orderId: number;
  amount: number;
  renderTrigger: (props: {
    loading: boolean;
    disabled: boolean;
    onClick: () => void;
  }) => ReactNode;
  onSuccess?: () => void;
  onError?: (message: string) => void;
};

export const PayWithRyftButton = (props: PayWithRyftButtonProps) => (
  <RyftCardPaymentProvider>
    <PayWithRyftButtonContent {...props} />
  </RyftCardPaymentProvider>
);

const PayWithRyftButtonContent = ({
  orderId,
  amount,
  renderTrigger,
  onSuccess,
  onError,
}: PayWithRyftButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    isInitiatingPaymentSession,
    isAttemptingPayment,
    paymentSessionId,
    cardFormValidationEvent,
    initiateOrderPaymentSession,
    payWithRyft,
  } = useRyftPayment();

  const refreshPayment = useRefreshRyftOrderPayment(orderId);

  const isBusy = isInitiatingPaymentSession || isAttemptingPayment;

  const handleDismiss = () => {
    if (isBusy) {
      return;
    }
    setIsOpen(false);
  };

  const handleInitiatePayment = async () => {
    if (isBusy) {
      return;
    }
    setIsOpen(true);
    setError(null);
    const response = await initiateOrderPaymentSession({ orderId });
    if (response.error) {
      const message =
        apiV2AssertError(response.error)?.response?.data.message ??
        "We couldn't prepare your card payment. Please try again.";
      setError(message);
      onError?.(message);
    }
  };

  const handlePay = async () => {
    if (isBusy) {
      return;
    }

    setError(null);
    const response = await payWithRyft();
    await refreshPayment();

    if (response.error) {
      setError(response.error.message);
      onError?.(response.error.message);
      return;
    }
    setIsOpen(false);
    onSuccess?.();
  };

  const renderContent = () => {
    if (isInitiatingPaymentSession) {
      return (
        <div
          className="flex min-h-48 items-center justify-center"
          role="status"
          aria-label="Preparing your card payment"
        >
          <Spinner />
        </div>
      );
    }

    if (error) {
      return <Alert message={error} type="error" />;
    }

    return <RyftCardForm />;
  };

  return (
    <>
      {renderTrigger({
        loading: isBusy,
        disabled: isBusy,
        onClick: handleInitiatePayment,
      })}
      <Dialog
        active={isOpen}
        onDismiss={handleDismiss}
        className="m-4 sm:h-fit"
      >
        <DialogNavigation
          title={`Pay ${formatCurrency(amount)} by card`}
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={handleDismiss}
        />
        <DialogContent>
          <div className="flex flex-col gap-4">
            {renderContent()}
            <div className="flex justify-end gap-2">
              <Button
                color="transparent"
                onClick={handleDismiss}
                disabled={isBusy}
              >
                Close
              </Button>
              {error && (
                <Button onClick={handleInitiatePayment} disabled={isBusy}>
                  Try again
                </Button>
              )}
              {!error && paymentSessionId && (
                <Button
                  onClick={handlePay}
                  loading={isAttemptingPayment}
                  disabled={isBusy || !cardFormValidationEvent?.isValid}
                >
                  Pay {formatCurrency(amount)}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const useRefreshRyftOrderPayment = (orderId: number) => {
  const queryClient = useQueryClient();
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();

  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [
          user?.uid,
          business?.id,
          ServerStateKeys.Checkout,
          "payment-status",
          orderId,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders],
      }),
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrdersLegacy],
      }),
    ]);
  }, [queryClient, user?.uid, business?.id, orderId]);
};
